import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob } from '@/lib/upload';
import { cn } from '@/utils/cn';

export const ProgramBannerField = ({
  imageKey,
  previewUrl,
  onChange,
  onUploadingChange,
  idPrefix,
  variant = 'program',
}: {
  imageKey: string | null;
  previewUrl: string | null;
  onChange: (next: { imageKey: string | null; previewUrl: string | null }) => void;
  onUploadingChange?: (uploading: boolean) => void;
  idPrefix: string;
  variant?: 'program' | 'service';
}) => {
  const { t } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingPreviewRef = useRef<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const copyNs = variant === 'service' ? 'services' : 'programs';

  const clearPendingPreview = () => {
    if (pendingPreviewRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(pendingPreviewRef.current);
    }
    pendingPreviewRef.current = null;
    setPendingPreview(null);
  };

  useEffect(
    () => () => {
      if (pendingPreviewRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(pendingPreviewRef.current);
      }
    },
    [],
  );

  const displayPreview = pendingPreview ?? previewUrl;

  return (
    <div aria-busy={uploading}>
      <Label htmlFor={`${idPrefix}-banner`}>{t(`${copyNs}.banner`)}</Label>
      <p className="mb-2 text-caption text-fg-secondary">{t(`${copyNs}.bannerHint`)}</p>
      {displayPreview ? (
        <div className="flex items-center gap-3">
          <div className="relative h-24 w-full max-w-md shrink-0 overflow-hidden rounded-lg">
            <img
              src={displayPreview}
              alt=""
              className={cn('h-full w-full object-cover', uploading && 'opacity-60')}
            />
            {uploading ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-surface/70">
                <Spinner className="size-5 text-brand" />
                <span className="text-caption text-fg-secondary">{tCommon('file.uploading')}</span>
              </div>
            ) : null}
          </div>
          {!uploading ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ imageKey: null, previewUrl: null })}
            >
              {t(`${copyNs}.bannerRemove`)}
            </Button>
          ) : null}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          id={`${idPrefix}-banner`}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {t(`${copyNs}.bannerAdd`)}
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        disabled={uploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          void (async () => {
            const localPreview = URL.createObjectURL(file);
            pendingPreviewRef.current = localPreview;
            setPendingPreview(localPreview);
            setUploading(true);
            onUploadingChange?.(true);
            try {
              const { key, url } = await uploadBlob({ purpose: 'content_image', blob: file });
              pendingPreviewRef.current = null;
              setPendingPreview(null);
              onChange({
                imageKey: key,
                previewUrl: url ?? localPreview,
              });
              if (url) {
                URL.revokeObjectURL(localPreview);
              }
            } catch (err) {
              clearPendingPreview();
              toast.error(
                err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'),
              );
            } finally {
              setUploading(false);
              onUploadingChange?.(false);
            }
          })();
        }}
      />
      {imageKey && !previewUrl && !uploading ? (
        <p className="mt-1 text-caption text-fg-secondary">{t(`${copyNs}.bannerSaved`)}</p>
      ) : null}
    </div>
  );
};

export const ProgramDayImageField = ({
  imageKey,
  previewUrl,
  onChange,
  idPrefix,
}: {
  imageKey: string | null;
  previewUrl: string | null;
  onChange: (next: { imageKey: string | null; previewUrl: string | null }) => void;
  idPrefix: string;
}) => {
  const { t } = useTranslation('programs');
  const { t: tErrors } = useTranslation('errors');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <Label htmlFor={`${idPrefix}-day-image`}>{t('dayDetails.image')}</Label>
      <p className="mb-2 text-caption text-fg-secondary">{t('dayDetails.imageHint')}</p>
      {previewUrl ? (
        <div className="flex items-center gap-3">
          <img
            src={previewUrl}
            alt=""
            className="size-20 shrink-0 rounded-lg object-cover"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange({ imageKey: null, previewUrl: null })}
          >
            {t('dayDetails.imageRemove')}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          id={`${idPrefix}-day-image`}
          onClick={() => inputRef.current?.click()}
        >
          {t('dayDetails.imageAdd')}
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          void (async () => {
            try {
              const { key, url } = await uploadBlob({ purpose: 'content_image', blob: file });
              onChange({
                imageKey: key,
                previewUrl: url ?? URL.createObjectURL(file),
              });
            } catch (err) {
              toast.error(
                err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'),
              );
            }
          })();
        }}
      />
      {imageKey && !previewUrl ? (
        <p className="mt-1 text-caption text-fg-secondary">{t('programs.bannerSaved')}</p>
      ) : null}
    </div>
  );
};
